import { UAItem } from "@/interfaces/CardItem";
import { SrcType } from "@/models/alt-arts";
import { AaMap } from "@/models/alt-arts";
import cardData from "@/data/union_arena/card_data.json";
import aaJson from "@/data/union_arena/aa_data.json";
import { reverseMap } from "@/lib/tcgplayer/getCardPrice";
import tcgplayerJson from "@/data/union_arena/tcgplayer_data.json";
import setsJson from "@/lib/games/unionArena/data/sets.json";
import { BaseCardDataManager } from "@/lib/games/base/getCardDataBase";
import { getCardImgproxyUrl } from "@/lib/imgproxy";
import { cardImageUrl, tcgplayerImageUrl } from "@/lib/cdn";

const aa = aaJson as AaMap;
const tcgplayerData = tcgplayerJson as Record<
  string,
  { name: string; card_number: string | null }
>;

class UnionArenaCardDataManager extends BaseCardDataManager<UAItem> {
  protected readonly cdnGame = "union-arena" as const;
  protected readonly imageVersion = "1.0";
  protected readonly defaultRegion = "en";

  /**
   * Union Arena publishes the Japanese printing under "ja" but the data calls
   * that region "asia_en". Anything else has no dedicated printing, so the
   * caller falls back to `defaultRegion`.
   */
  protected getCorrectedRegion(region: SrcType | ""): string {
    const r = region.toLowerCase();
    if (r === "en") return "en";
    if (r === "asia_en") return "ja";
    return "";
  }

  private CACHED_UA_TO_TCGPLAYER_ID_MAP: { [key: string]: number } = {};

  constructor() {
    super(aa);
    // Initialize CARD_DATA with cleaned data
    this.CARD_DATA = this.clean(cardData);
  }

  public getTcgPlayerCardName(tcgplayerId: string): string {
    return tcgplayerData[tcgplayerId]?.name ?? "";
  }

  public getUAIdFromTcgPlayerId(tcgplayerId: string): string | undefined {
    let cardNumber = tcgplayerData[tcgplayerId]?.card_number ?? "";
    if (cardNumber.includes("/")) {
      cardNumber = cardNumber.split("/")[1];
    }
    return this.getCardNumber(cardNumber);
  }

  public getUAToTCGPlayerIdMap(): Record<string, number> {
    if (Object.keys(this.CACHED_UA_TO_TCGPLAYER_ID_MAP).length > 0) {
      return this.CACHED_UA_TO_TCGPLAYER_ID_MAP;
    }
    for (const [_key, cards] of Object.entries(this.aa)) {
      for (const card of cards) {
        if (
          card.src.toLowerCase() === "en" &&
          card.tcgplayer_id &&
          card.tcgplayer_id !== ""
        ) {
          this.CACHED_UA_TO_TCGPLAYER_ID_MAP[card.id] = parseInt(
            card.tcgplayer_id,
          );
        }
      }
    }
    return this.CACHED_UA_TO_TCGPLAYER_ID_MAP;
  }

  public getFullCardId(cardNumber: string): string | undefined {
    if (cardNumber in this.aa) {
      return this.aa[cardNumber][0].id.replace("_", "/");
    }
    return undefined;
  }

  public getTCGPlayerCardImage(
    id: string,
    size: "original" | "316x440" | "158x220" | "90x125" = "original",
  ): string {
    return getCardImgproxyUrl(tcgplayerImageUrl(id), size);
  }

  public getCardNumber(cardId: string): string {
    let cardNumber = cardId;

    const underscoreCount = (cardId.match(/_/g) || []).length;
    if (underscoreCount === 2) {
      // card id like UA15BT_SAO-1-087_a1
      const [_UASet, baseCardId, _altArtId] = cardId.split("_");
      cardNumber = baseCardId;
    } else if (underscoreCount === 1) {
      // id is like SAO-1-087_p1
      const [baseCardId, _altArtId] = cardId.split("_");
      cardNumber = baseCardId;
    } else {
      // simple card version like SAO-1-087
      cardNumber = cardId;
    }
    return cardNumber;
  }

  public isCardNumber(cardId: string): boolean {
    return !cardId.includes("_");
  }

  public getCardId(cardId: string): string | undefined {
    if (this.isCardNumber(cardId)) {
      const cardNumber = cardId;
      if (cardNumber in this.aa && this.aa[cardNumber].length > 0) {
        // assume first card is en and the base rare card
        const firstCard = this.aa[cardNumber][0];
        return firstCard.id;
      }
    } else {
      return cardId;
    }
  }

  public getSetFullName(set: string): string {
    return setsJson[set as keyof typeof setsJson] ?? set;
  }

  protected clean(data: Record<string, UAItem>): Record<string, UAItem> {
    let cleanedData: Record<string, UAItem> = {};
    for (const [k, v] of Object.entries(data)) {
      // Create a deep copy of the current item
      let item: UAItem = structuredClone(v);

      // Convert cost to a number if it's a string
      if (typeof item.energy_cost === "string") {
        const cost = this.getIntFromStr(item.energy_cost);
        item.cost = cost;
      }

      if (item.effect === null) {
        item.effect = "";
      }

      // Ensure src is either "en" or "ja"
      if (item.src !== "en" && item.src !== "ja") {
        item.src = "en"; // Default to English if not specified
      }

      // Add the cleaned item to the cleanedData object
      cleanedData[k] = item;
    }

    return cleanedData;
  }

  public cleanCardData(): Record<string, UAItem> {
    // Check if CARD_DATA array is not empty
    if (Object.keys(this.CARD_DATA).length === 0) {
      this.CARD_DATA = this.clean(cardData);
    }

    // Return an empty array if CARD_DATA is empty
    return this.CARD_DATA;
  }

  public getCardData(cardId: string): UAItem | undefined {
    if (!cardId) {
      return undefined;
    }
    let cardNumber = this.getCardNumber(cardId);
    let cardData = this.cleanCardData()[cardNumber];
    return cardData;
  }

  public getAAFromCardId(aaCardId: string): any {
    let cardNumber, srcChar, count;

    if (aaCardId.includes("_")) {
      // Union Arena format: IMS_AP04_a0 or IMS_AP04_a10
      const parts = aaCardId.split("_");
      if (parts.length === 2) {
        cardNumber = `${parts[0]}`; // IMS_AP04
        srcChar = parts[1].charAt(0); // 'a' from a10
        count = parseInt(parts[1].slice(1), 10); // 10 from a10
      } else if (parts.length === 3) {
        cardNumber = `${parts[0]}_${parts[1]}`; // IMS_AP04
        srcChar = parts[2].charAt(0); // 'a' from a10
        count = parseInt(parts[2].slice(1), 10); // 10 from a10
      } else {
        throw new Error(`Invalid format for aaCardId: ${aaCardId}`);
      }
    } else {
      // defaults
      cardNumber = aaCardId;
      srcChar = "";
      count = 0;
    }

    return this.getAA(cardNumber, srcChar, count);
  }

  protected getIntFromStr(value: string): number {
    let ret = 0;
    if (
      value !== "-" &&
      value !== "" &&
      value !== null &&
      value !== undefined
    ) {
      ret = parseInt(value);
    }
    return ret;
  }
}

// Create singleton instance
const unionArenaManager = new UnionArenaCardDataManager();

// Export functions that delegate to the manager
export function getTcgPlayerCardName(tcgplayerId: string): string {
  return unionArenaManager.getTcgPlayerCardName(tcgplayerId);
}

export function getUAIdFromTcgPlayerId(
  tcgplayerId: string,
): string | undefined {
  return unionArenaManager.getUAIdFromTcgPlayerId(tcgplayerId);
}

export function getUAToTCGPlayerIdMap(): Record<string, number> {
  return unionArenaManager.getUAToTCGPlayerIdMap();
}

export function getFullCardId(cardNumber: string): string | undefined {
  return unionArenaManager.getFullCardId(cardNumber);
}

export function getTCGPlayerCardImage(
  id: string,
  size: "original" | "316x440" | "158x220" | "90x125" = "original",
): string {
  return unionArenaManager.getTCGPlayerCardImage(id, size);
}

export function cleanCardData(): Record<string, UAItem> {
  return unionArenaManager.cleanCardData();
}

export function getCardImage(
  cardId: string,
  size: "original" | "316x440" | "158x220" | "90x125" = "original",
  src: SrcType | "" = "",
): string {
  return unionArenaManager.getCardImage(cardId, size, src);
}

export function getCardData(cardId: string): UAItem | undefined {
  return unionArenaManager.getCardData(cardId);
}

export function getCardNumber(cardId: string): string {
  return unionArenaManager.getCardNumber(cardId);
}

export function isCardNumber(cardId: string): boolean {
  return unionArenaManager.isCardNumber(cardId);
}

export function getCardId(cardId: string): string | undefined {
  return unionArenaManager.getCardId(cardId);
}

export function getAACardIdData(aaCardId: string): UAItem | undefined {
  return unionArenaManager.getAACardIdData(aaCardId);
}

export function getAACardId(aa: any): string {
  return unionArenaManager.getAACardId(aa);
}

export function getAAFromCardId(aaCardId: string): any {
  return unionArenaManager.getAAFromCardId(aaCardId);
}

export function getAA(cardNumber: string, srcChar: string, count: number): any {
  return unionArenaManager.getAA(cardNumber, srcChar, count);
}

export function getAltArts(cardId: string, showAAs: boolean): any[] {
  return unionArenaManager.getAltArts(cardId, showAAs);
}

export function getSetFullName(set: string): string {
  return unionArenaManager.getSetFullName(set);
}
